'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Signature } from 'lucide-react';
import { useOutputStyles } from '@/shared/api/hooks/use-output-styles';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import {
  OutputContent,
  getOutputSubtitle,
} from '../settings-content/OutputContent';
import { SettingsSectionRow } from './SettingsSection';

interface OutputStyleCarouselProps {
  selectedOutputStyleId: number | null;
  onOutputStyleChange: (styleId: number | null) => void;
  hasChanges?: boolean;
  // Project reference props
  projectId?: string;
  outputProjectReferenceIds?: string[] | null;
  onOutputProjectReferencesChange?: (ids: string[] | null) => void;
  onNavigateToReferences?: () => void;
}

export function OutputStyleCarousel({
  selectedOutputStyleId,
  onOutputStyleChange,
  hasChanges = false,
  projectId,
  outputProjectReferenceIds,
  onOutputProjectReferencesChange,
  onNavigateToReferences,
}: OutputStyleCarouselProps) {
  const t = useTranslations('render.settings.output');
  const { data: outputStyles = [] } = useOutputStyles();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const subtitle = getOutputSubtitle(
    selectedOutputStyleId,
    outputStyles,
    t,
    outputProjectReferenceIds
  );

  // The row stays unbadged and always opens the picker, even for locked users:
  // the lock is surfaced inside the sheet (Custom style row + per-preset
  // Designer badges), where a locked tap opens the Designer Pass paywall.
  // Mirrors Apple/Android, where the picker is browsable by everyone.
  return (
    <>
      <SettingsSectionRow
        icon={Signature}
        title={t('title')}
        subtitle={subtitle}
        hasChanges={hasChanges}
        onClick={() => setIsSheetOpen(true)}
      />

      <NestedSheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('title')}</NestedSheetTitle>
          </NestedSheetHeader>

          <NestedSheetBody className="px-4">
            <OutputContent
              selectedOutputStyleId={selectedOutputStyleId}
              onOutputStyleChange={onOutputStyleChange}
              projectId={projectId}
              outputProjectReferenceIds={outputProjectReferenceIds}
              onOutputProjectReferencesChange={onOutputProjectReferencesChange}
              onNavigateToReferences={onNavigateToReferences}
              variant="compact"
              closeOnSelect={() => setIsSheetOpen(false)}
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
    </>
  );
}
